import { useState, useLayoutEffect, useRef } from 'react'

import useStableResult from './useStableResult'

/**
 * Return type for useDebounceValue hook
 * [debouncedValue, isDebouncing, override]
 */
type UseDebounceValueReturn<T> = readonly [
  T, // debouncedValue: the debounced value
  boolean, // isDebouncing: whether currently debouncing
  (value: T) => void, // override: function to manually set value and clear timeout
]

/**
 * Hook that debounces a value with configurable wait time
 *
 * @param value - The value to debounce
 * @param wait - Debounce delay in milliseconds (default: 300)
 * @returns Tuple of [debouncedValue, isDebouncing, override]
 *
 * @example
 * const [debouncedValue, isDebouncing, override] = useDebounceValue(searchTerm, 500)
 *
 * // Array destructuring (most common usage)
 * const [debouncedSearchTerm] = useDebounceValue(searchTerm, 300)
 */
const useDebounceValue = <T>(
  value: T,
  wait = 300,
): UseDebounceValueReturn<T> => {
  const [val, setVal] = useState<T>(value)

  const timeoutRef = useRef<NodeJS.Timeout>()

  useLayoutEffect(() => {
    const onTimeout = () => setVal(value)
    timeoutRef.current = setTimeout(onTimeout, wait)
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current)
      }
    }
  }, [wait, value])

  const isDebouncing: boolean = val !== value

  const override = (newValue: T): void => {
    setVal(newValue)
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current)
    }
  }

  return useStableResult(val, isDebouncing, override)
}

export default useDebounceValue
